import React, { useEffect, useState, useCallback } from 'react';
import { zuweisungService } from '../../services/zuweisung.service';
import { Zuweisung, ZuweisungFilter } from '../../types';
import { formatDate } from '../../utils/dateUtils';

interface ZuweisungListProps {
  onEdit: (zuweisung: Zuweisung) => void;
  onDelete: (zuweisung: Zuweisung) => void;
  onCreate: () => void;
  refreshTrigger?: number;
  filter?: ZuweisungFilter;
}

const ZuweisungList: React.FC<ZuweisungListProps> = ({
  onEdit,
  onDelete,
  onCreate,
  refreshTrigger,
  filter
}) => {
  const [zuweisungen, setZuweisungen] = useState<Zuweisung[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);

  const fetchZuweisungen = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await zuweisungService.getAllZuweisungen(filter, page, limit);
      // Determine if result is array (old format) or object (new format)
      if (Array.isArray(result)) {
        setZuweisungen(result);
        setTotal(result.length);
      } else {
        setZuweisungen(result.data);
        setTotal(result.meta.total);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Zuweisungen');
    } finally {
      setLoading(false);
    }
  }, [filter, page, limit]);

  useEffect(() => {
    fetchZuweisungen();
  }, [refreshTrigger, fetchZuweisungen]);

  // Reset page to 1 when filter changes
  useEffect(() => {
    setPage(1);
  }, [filter]);

  const getZeitslotLabel = (zeitslot: string): string => {
    return zeitslot === 'VORMITTAG' ? 'Vormittag' : 'Nachmittag';
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(parseInt(e.target.value));
    setPage(1); // Reset to first page when limit changes
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && zuweisungen.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-600">Lädt...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-gray-800">Zuweisungen Liste</h2>
        <button
          onClick={onCreate}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
        >
          Neue Zuweisung
        </button>
      </div>

      {zuweisungen.length === 0 && !loading ? (
        <div className="text-center py-8 text-gray-500">
          Keine Zuweisungen vorhanden. Erstellen Sie eine neue Zuweisung.
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden flex flex-col">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Arzt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Praxis
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zeitslot
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notizen
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {zuweisungen.map((zuweisung) => (
                  <tr key={zuweisung.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {zuweisung.arzt
                        ? `${zuweisung.arzt.vorname} ${zuweisung.arzt.nachname}`
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {zuweisung.praxis?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(zuweisung.datum)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${zuweisung.zeitslot === 'VORMITTAG'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                        }`}>
                        {getZeitslotLabel(zuweisung.zeitslot)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {zuweisung.notizen || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => onEdit(zuweisung)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => onDelete(zuweisung)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm text-gray-700 mr-4">
                Zeige
              </span>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 p-1"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
              <span className="text-sm text-gray-700 ml-2">
                Einträge
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 mr-4">
                Seite {page} von {totalPages || 1} ({total} Gesamt)
              </span>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className={`px-3 py-1 rounded-md text-sm font-medium ${page === 1
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
              >
                Zurück
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className={`px-3 py-1 rounded-md text-sm font-medium ${page >= totalPages
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZuweisungList;
